import { Seniority, WorkType } from '@hr-outsourcing/shared';
import { JOBS_FILTER_SEPARATOR, JOBS_PAGE_PARAM, JOBS_SALARY_BAND_STARTS, JOBS_SEARCH_PARAMS } from '../constants';
import type { CareersJobListing, CareersJobSearch } from '@/types/careers';

// The listing search in the URL, shared by the visitor's /jobs and the candidate's listings page.

export const isOneOf = <T extends string>(values: Record<string, T>, value: string | null): value is T =>
  !!value && (Object.values(values) as string[]).includes(value);

// The values of a multi-value filter in the URL (`city=Ankara,İzmir`), empty ones dropped.
const readList = (params: URLSearchParams, key: string) =>
  [...new Set((params.get(key) ?? '').split(JOBS_FILTER_SEPARATOR).map((value) => value.trim()))].filter(Boolean);

/** Where a salary band ends (the next step), or undefined for the last band. */
export const salaryBandEnd = (start: number) => JOBS_SALARY_BAND_STARTS.find((step) => step > start);

const readSalaryBands = (params: URLSearchParams): number[] => {
  const bands = readList(params, JOBS_SEARCH_PARAMS.salary)
    .map(Number)
    .filter((start) => JOBS_SALARY_BAND_STARTS.includes(start));
  if (bands.length > 0) return bands;
  // An older link with "at least X": the bands that reach X.
  const minSalary = Number(params.get(JOBS_SEARCH_PARAMS.minSalary));
  if (!(minSalary > 0)) return [];
  return JOBS_SALARY_BAND_STARTS.filter((start) => (salaryBandEnd(start) ?? Infinity) > minSalary);
};

export const readJobSearch = (params: URLSearchParams): CareersJobSearch => {
  const q = params.get(JOBS_SEARCH_PARAMS.q);
  const city = readList(params, JOBS_SEARCH_PARAMS.city);
  const workType = readList(params, JOBS_SEARCH_PARAMS.workType).filter((value) => isOneOf(WorkType, value));
  const seniority = readList(params, JOBS_SEARCH_PARAMS.seniority).filter((value) => isOneOf(Seniority, value));
  const salary = readSalaryBands(params);
  const country = readList(params, JOBS_SEARCH_PARAMS.country).map((code) => code.toUpperCase());
  const page = Number(params.get(JOBS_PAGE_PARAM));
  return {
    ...(q && { q }),
    ...(city.length > 0 && { city }),
    ...(workType.length > 0 && { workType: workType as WorkType[] }),
    ...(seniority.length > 0 && { seniority: seniority as Seniority[] }),
    ...(salary.length > 0 && { salary }),
    ...(country.length > 0 && { country }),
    ...(page > 1 && { page }),
  };
};

// The listing's shown salary range overlaps the band (the server's rule).
const inSalaryBand = (salary: CareersJobListing['salary'], start: number) => {
  if (!salary) return false;
  const low = salary.min ?? salary.max ?? 0;
  const high = salary.max ?? salary.min ?? 0;
  const end = salaryBandEnd(start);
  return high >= start && (end == null || low < end);
};

/**
 * A listing against the search, in the browser (the candidate's listings page searches its own
 * list). `searchTerms`: more the text search looks in, as the server's search does (the
 * position's title, all its required skills; the card carries only the first few skills).
 */
export const matchesJobSearch = (listing: CareersJobListing, search: CareersJobSearch, searchTerms: string[] = []) => {
  const text = search.q?.toLocaleLowerCase('tr');
  const haystack = [listing.title, listing.companyName, ...(listing.skills ?? []), ...searchTerms]
    .join(' ')
    .toLocaleLowerCase('tr');
  const cities = search.city?.map((city) => city.toLocaleLowerCase('tr'));
  return (
    (!text || haystack.includes(text)) &&
    (!cities || listing.locations.some((l) => cities.includes(l.toLocaleLowerCase('tr')))) &&
    (!search.workType || search.workType.includes(listing.workType)) &&
    (!search.seniority || search.seniority.includes(listing.seniority as Seniority)) &&
    (!search.salary || search.salary.some((start) => inSalaryBand(listing.salary, start)))
  );
};

/** A search as API parameters: several values of a filter comma-separated (`city=Ankara,İzmir`). */
export const jobSearchParams = (search: CareersJobSearch) =>
  Object.fromEntries(
    Object.entries(search).map(([key, value]) => [key, Array.isArray(value) ? value.join(JOBS_FILTER_SEPARATOR) : value])
  );
